import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link, useParams } from 'react-router-dom';
import { comics, chapters } from '@/mocks/comics';
import { useState } from 'react';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);
  const comicChapters = id ? chapters[id] || [] : [];

  const [activeTab, setActiveTab] = useState<'chapters' | 'reviews'>('chapters');

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">未找到该漫画</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const sortChapters = [...comicChapters].sort((a, b) => b.number - a.number);

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-24 md:pt-28 pb-10 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs font-label text-foreground-500 mb-8">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <span>/</span>
              <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors">{comic.category}</Link>
              <span>/</span>
              <span className="text-foreground-900 font-medium">{comic.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-2xl overflow-hidden bg-background-100">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 作者${comic.author} - 韩漫漫画网`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium">
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-label">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 mb-4">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600 mb-5">
                  <span>作者：{comic.author}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span>更新：{comic.updateDate}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-accent-500"></i>
                    </div>
                    <span className="font-semibold">{comic.rating}</span>
                  </div>
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-6 max-w-xl">
                  {comic.description}
                </p>

                <div className="flex flex-wrap items-center gap-6 mb-6">
                  <div className="text-center">
                    <p className="font-heading text-xl font-bold text-foreground-950">{comic.views}</p>
                    <p className="text-xs text-foreground-500 mt-1">总人气</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-xl font-bold text-foreground-950">{comic.rating}</p>
                    <p className="text-xs text-foreground-500 mt-1">评分</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-xl font-bold text-foreground-950">{comic.latestChapterNum} 话</p>
                    <p className="text-xs text-foreground-500 mt-1">连载至</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to={`/comic/${comic.id}`}
                    className="px-6 py-2.5 bg-foreground-950 text-background-50 font-label font-semibold text-sm rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <button
                    className="px-6 py-2.5 border border-foreground-300 text-foreground-700 font-label text-sm rounded-full hover:border-foreground-400 hover:text-foreground-900 transition-colors whitespace-nowrap"
                  >
                    加入书架
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-foreground-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <span className="inline-block text-xs font-label text-foreground-400 uppercase tracking-wider mb-3">
              作品介绍
            </span>
            <p className="font-heading text-xl md:text-2xl text-background-50 leading-relaxed max-w-3xl mx-auto mb-8">
              {comic.description}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-6 py-2.5 rounded-full font-label text-sm transition-colors whitespace-nowrap ${
                  activeTab === 'chapters'
                    ? 'bg-background-50 text-foreground-950'
                    : 'border border-background-300/30 text-background-50 hover:border-background-50'
                }`}
              >
                查看章节目录
              </button>
              <Link
                to={`/category/${comic.categorySlug}`}
                className="px-6 py-2.5 rounded-full border border-background-300/30 text-background-50 font-label text-sm hover:border-background-50 transition-colors whitespace-nowrap"
              >
                查看同类作品
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10">
              {[
                { icon: 'ri-eye-line', label: '总人气', value: comic.views },
                { icon: 'ri-star-fill', label: '评分', value: `${comic.rating}` },
                { icon: 'ri-file-list-3-line', label: '连载至', value: `${comic.latestChapterNum} 话` },
                { icon: 'ri-user-line', label: '作者', value: comic.author },
                { icon: 'ri-calendar-check-line', label: '最近更新', value: comic.updateDate },
              ].map((stat, i) => (
                <div key={i} className="bg-foreground-800/50 rounded-xl p-4 text-center">
                  <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center">
                    <i className={`${stat.icon} text-lg text-accent-400`}></i>
                  </div>
                  <p className="font-heading text-sm font-bold text-background-50">{stat.value}</p>
                  <p className="text-xs text-foreground-400 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 mb-8 bg-background-100 rounded-full p-1 w-fit">
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-5 py-2 rounded-full text-sm font-label transition-all whitespace-nowrap ${
                  activeTab === 'chapters'
                    ? 'bg-background-50 text-foreground-950 font-semibold'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                章节目录
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`px-5 py-2 rounded-full text-sm font-label transition-all whitespace-nowrap ${
                  activeTab === 'reviews'
                    ? 'bg-background-50 text-foreground-950 font-semibold'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                读者评论
              </button>
            </div>

            {activeTab === 'chapters' && (
              <div>
                {sortChapters.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {sortChapters.map((chapter) => (
                      <Link
                        key={chapter.id}
                        to={`/comic/${comic.id}`}
                        className="flex items-center justify-between px-4 py-3 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors group"
                      >
                        <span className="text-sm text-foreground-800 group-hover:text-primary-600 font-label">
                          第{chapter.number}话
                        </span>
                        <span className="text-xs text-foreground-400">{chapter.updateDate}</span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                      <i className="ri-file-list-3-line text-3xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500">章节正在更新中，敬请期待</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-4">
                <div className="bg-background-100 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-foreground-500 font-label text-sm font-semibold">
                      漫
                    </div>
                    <div>
                      <p className="text-sm font-label font-semibold text-foreground-900">漫画迷小月</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-xs text-accent-500"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    这部漫画真的太好看了！画风精美，剧情紧凑，每一话都让人期待下一话。作者对人物的刻画非常细腻，强烈推荐！
                  </p>
                </div>
                <div className="bg-background-100 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-foreground-500 font-label text-sm font-semibold">
                      追
                    </div>
                    <div>
                      <p className="text-sm font-label font-semibold text-foreground-900">追更达人阿杰</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-xs text-accent-500"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    从第一话追到现在，见证了主角一步步的成长。韩漫漫画网的更新速度真的没话说，每次都是第一时间就能看到最新话！
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <p className="font-heading text-lg text-foreground-950 mb-6">
              继续阅读
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Link
                to={`/comic/${comic.id}`}
                className="px-6 py-2.5 border border-foreground-300 text-foreground-600 font-label text-sm rounded-full hover:border-foreground-400 transition-colors whitespace-nowrap"
              >
                上一章
              </Link>
              <Link
                to={`/comic/${comic.id}`}
                className="px-8 py-3 bg-primary-500 text-background-50 font-label font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                阅读下一话
              </Link>
              <Link
                to={`/comic/${comic.id}`}
                className="px-6 py-2.5 border border-foreground-300 text-foreground-600 font-label text-sm rounded-full hover:border-foreground-400 transition-colors whitespace-nowrap"
              >
                目录
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'http://j3zb.com/' },
              { '@type': 'ListItem', position: 2, name: comic.category, item: `http://j3zb.com/category/${comic.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: comic.title, item: `http://j3zb.com/comic/${comic.id}` },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Book',
            '@id': `http://j3zb.com/comic/${comic.id}`,
            name: comic.title,
            author: { '@type': 'Person', name: comic.author },
            description: comic.description,
            genre: comic.category,
            inLanguage: 'zh-CN',
            isAccessibleForFree: true,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: comic.rating,
              bestRating: 10,
              ratingCount: Math.floor(Math.random() * 50000) + 10000,
            },
            image: comic.coverImage,
            dateModified: '2026-07-03',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            '@id': `http://j3zb.com/comic/${comic.id}`,
            url: `http://j3zb.com/comic/${comic.id}`,
            name: `${comic.title} - 韩漫漫画网`,
            description: `${comic.title}，${comic.category}漫画，作者${comic.author}，${comic.description.slice(0, 100)}...`,
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫漫画网',
              url: 'http://j3zb.com/',
            },
            primaryImageOfPage: {
              '@type': 'ImageObject',
              url: comic.coverImage,
              caption: `${comic.title} - 韩漫漫画网在线阅读`,
            },
          }),
        }}
      />
    </>
  );
}